import type { OrganizationEntry } from '@obras/shared';
import {
  formatBytes,
  MAX_STORAGE_LIMIT_BYTES,
  maskCnpj,
  MIN_STORAGE_LIMIT_BYTES,
} from '@obras/shared';
import { Check } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { TextField } from '../../components/ui/Field.js';
import { FormError } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useUpdateOrganization } from './api.js';

const GIGA = 1024 * 1024 * 1024;

/**
 * Edição de uma organização, pela plataforma.
 *
 * O que se edita aqui é o que a plataforma decide **sobre** o órgão — nome,
 * CNPJ, periodicidade padrão de visita e espaço em nuvem. O que é decisão de
 * dentro do órgão continua na tela de configurações dele.
 *
 * O espaço aparece em gigabytes, e não em bytes. Ninguém contrata "107374182400
 * bytes", e um campo em bytes convida ao erro de uma casa decimal — que aqui é a
 * diferença entre 10 GB e 100 GB.
 */
export function OrganizationFormModal({
  organization,
  onClose,
}: {
  organization: OrganizationEntry | null;
  onClose: () => void;
}) {
  const update = useUpdateOrganization();

  const [name, setName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [intervalo, setIntervalo] = useState('');
  const [limiteGb, setLimiteGb] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* Recarrega os campos toda vez que outra organização é aberta. */
  const [carregadaDe, setCarregadaDe] = useState<string | null>(null);
  if (organization && carregadaDe !== organization.id) {
    setCarregadaDe(organization.id);
    setName(organization.name);
    setCnpj(organization.cnpj ? maskCnpj(organization.cnpj) : '');
    setIntervalo(String(organization.defaultVisitIntervalDays));
    setLimiteGb(String(Math.round(organization.storageLimitBytes / GIGA)));
    setFormError(null);
    setFieldErrors({});
  }

  function fechar(): void {
    setCarregadaDe(null);
    onClose();
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!organization) return;

    setFormError(null);
    setFieldErrors({});

    const gb = Number(limiteGb.replace(',', '.'));
    if (!Number.isFinite(gb) || gb <= 0) {
      setFieldErrors({ storageLimitBytes: 'Informe o espaço em GB.' });
      return;
    }

    try {
      await update.mutateAsync({
        id: organization.id,
        name: name.trim(),
        cnpj: cnpj.trim() === '' ? undefined : cnpj.replace(/\D/g, ''),
        defaultVisitIntervalDays: Number(intervalo) || organization.defaultVisitIntervalDays,
        storageLimitBytes: Math.round(gb * GIGA),
      });
      fechar();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível salvar a organização.');
      }
    }
  }

  const usado = organization?.storageUsedBytes ?? 0;
  const limitePedido = Math.round((Number(limiteGb.replace(',', '.')) || 0) * GIGA);
  const abaixoDoUso = limitePedido > 0 && limitePedido < usado;

  return (
    <Modal
      open={organization !== null}
      onClose={fechar}
      title="Editar organização"
      description="Dados cadastrais e espaço em nuvem contratado."
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={update.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="organizacao-form"
            type="submit"
            loading={update.isPending}
            icon={<Check size={16} strokeWidth={1.5} />}
          >
            Salvar
          </Button>
        </>
      }
    >
      <form
        id="organizacao-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        <TextField
          label="Nome da organização"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
        />

        <TextField
          label="CNPJ"
          mono
          inputMode="text"
          placeholder="00.000.000/0000-00"
          value={cnpj}
          onChange={(event) => setCnpj(maskCnpj(event.target.value))}
          {...(fieldErrors['cnpj'] ? { error: fieldErrors['cnpj'] } : {})}
        />

        <TextField
          label="Intervalo padrão de visita"
          type="number"
          min={1}
          max={365}
          hint="Em dias. Cada obra pode definir o seu próprio."
          value={intervalo}
          onChange={(event) => setIntervalo(event.target.value)}
          {...(fieldErrors['defaultVisitIntervalDays']
            ? { error: fieldErrors['defaultVisitIntervalDays'] }
            : {})}
        />

        <div>
          <TextField
            label="Espaço em nuvem"
            type="number"
            min={MIN_STORAGE_LIMIT_BYTES / GIGA}
            max={MAX_STORAGE_LIMIT_BYTES / GIGA}
            mono
            hint={`Em GB. Vale para foto, documento, projeto e vídeo somados. Em uso hoje: ${formatBytes(usado)}.`}
            value={limiteGb}
            onChange={(event) => setLimiteGb(event.target.value)}
            {...(fieldErrors['storageLimitBytes']
              ? { error: fieldErrors['storageLimitBytes'] }
              : {})}
          />

          {/*
            Aviso, e não impedimento: baixar o limite abaixo do que já está
            ocupado é decisão comercial legítima — o que estava lá continua
            servido, e novos envios param até a conta caber. Impedir seria
            confundir "cobrar diferente" com "apagar arquivo".
          */}
          {abaixoDoUso ? (
            <p className="mt-1.5 text-[13px] text-warning">
              Abaixo do que a organização já ocupa. Nada é apagado, mas novos envios ficam
              bloqueados até liberar espaço.
            </p>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
